// neo_cogym — MicroPython Raw REPL 클라이언트 (P2 핵심)
// T1 분석(uploader.js PySerial)에서 확인한 제어 프로토콜을 웹으로 1:1 재현:
//   0x01 = Raw REPL 진입, 0x04 = 실행(EOF), 0x02 = Raw REPL 종료, 0x03 = KeyboardInterrupt
// 코드는 256B 청크로 분할 전송. 컴파일 없음 — 코드/명령만 스트리밍.
// 전송 계층(Transport)에만 의존하므로 WebSerial·릴레이 양쪽에서 동일 동작.

import type { Transport } from "./transport";
import { toBytes, dec } from "./transport";

const CTRL = {
  enterRaw: 0x01,
  exec: 0x04,
  exitRaw: 0x02,
  interrupt: 0x03,
} as const;

const CHUNK_SIZE = 256;
const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

export interface ReplOptions {
  /** 출력(stdout/stderr) 스트림 콜백 */
  onOutput?: (text: string) => void;
}

export class ReplClient {
  private buf = "";
  private detach: (() => void) | null = null;

  constructor(private transport: Transport, private opts: ReplOptions = {}) {}

  /** 수신 버퍼링 시작 */
  attach() {
    if (this.detach) return;
    this.detach = this.transport.onData((chunk) => {
      const text = dec.decode(chunk, { stream: true });
      this.buf += text;
      this.opts.onOutput?.(text);
    });
  }

  release() {
    this.detach?.();
    this.detach = null;
  }

  private async writeByte(b: number) {
    await this.transport.write(new Uint8Array([b]));
  }

  /** 버퍼에 needle 이 나타날 때까지 대기(타임아웃 시 false) */
  private async waitFor(needle: string, timeoutMs = 5000): Promise<boolean> {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      if (this.buf.includes(needle)) return true;
      await delay(20);
    }
    return false;
  }

  private clearBuf() {
    this.buf = "";
  }

  /**
   * 코드를 Raw REPL 로 실행(즉시 실행, 저장 안 함).
   * uploader.js PySerial.codeRun 의 웹 포팅: 0x01 → 청크 전송 → 0x04.
   */
  async run(code: string): Promise<void> {
    this.attach();
    await this.writeByte(CTRL.enterRaw);
    await delay(100);

    const bytes = toBytes(code);
    for (let off = 0; off < bytes.length; off += CHUNK_SIZE) {
      await this.transport.write(bytes.subarray(off, off + CHUNK_SIZE));
      await delay(10);
    }
    await this.writeByte(CTRL.exec);
  }

  /**
   * 실행 정지: CTRL-C ×3 → 리셋 시퀀스(0x03,0x01,0x04) → 0x02.
   * (uploader.js PySerial.pause 포팅)
   */
  async stop(): Promise<void> {
    for (let i = 0; i < 3; i++) {
      await this.writeByte(CTRL.interrupt);
      await delay(100);
    }
    await delay(200);
    await this.transport.write(new Uint8Array([CTRL.interrupt, CTRL.enterRaw, CTRL.exec]));
    await delay(500);
    await this.writeByte(CTRL.exitRaw);
    await delay(300);
  }

  /**
   * 코드를 main.py 로 영구 저장 후 실행(uploader.js PySerial.codeUpload 포팅).
   * 리셋 → 프롬프트 대기 → 파일쓰기 명령 → 실행.
   */
  async saveToMainPy(code: string): Promise<{ success: boolean; error?: string }> {
    this.attach();
    // 1) 시스템 리셋
    this.clearBuf();
    await this.transport.write(new Uint8Array([CTRL.interrupt, CTRL.enterRaw, CTRL.exec]));
    await this.waitFor("CTRL-B to exit", 5000);
    await this.writeByte(CTRL.exitRaw);

    // 2) >>> 프롬프트 대기
    this.clearBuf();
    await this.waitFor(">>>", 5000);

    // 3) 파일 쓰기 (개행/따옴표 이스케이프)
    const escaped = code.replace(/[\\\r\n']/g, (c) =>
      c === "\\" ? "\\\\" : c === "\r" ? "\\r" : c === "\n" ? "\\n" : "\\'",
    );
    const cmd = `with open('main.py','w') as f: f.write('${escaped}')`;
    this.clearBuf();
    await this.writeByte(CTRL.enterRaw);
    await delay(100);
    await this.transport.write(toBytes(cmd));
    await this.writeByte(CTRL.exec);

    // 4) OK/에러 판정
    const ok = await this.waitFor("OK", 5000);
    if (this.buf.includes("Traceback")) {
      return { success: false, error: this.buf };
    }
    if (!ok) return { success: false, error: "upload timeout" };

    await this.writeByte(CTRL.exitRaw);
    await delay(200);

    // 5) 저장 직후 실행
    await this.writeByte(CTRL.enterRaw);
    await delay(100);
    await this.transport.write(toBytes("exec(open('main.py').read(), {'__name__':'__main__'})\n"));
    await this.writeByte(CTRL.exec);
    await delay(200);
    await this.writeByte(CTRL.exitRaw);
    return { success: true };
  }
}
